export type StepNumber = 1 | 2 | 3 | 4 | 5 | 6;

export interface WizardDaten {
  // Step 1: Kunde & Fahrer
  mieter_vorname: string;
  mieter_nachname: string;
  mieter_strasse: string;
  mieter_plz: string;
  mieter_ort: string;
  mieter_telefon: string;
  mieter_email: string;
  mieter_geburtsdatum: string;
  mieter_fuehrerschein_nr: string;
  fahrer_abweichend: boolean;
  fahrer_vorname: string;
  fahrer_nachname: string;
  fahrer_fuehrerschein_nr: string;
  fahrer_geburtsdatum: string;
  // Step 2: Schaden & Versicherung
  versicherung_name: string;
  versicherung_adresse: string;
  versicherung_plz_ort: string;
  schadennummer: string;
  unfalldatum: string;
  unfallort: string;
  haftungsquote: number;
  // Step 3: Fahrzeuge
  geschaedigtes_kfz_kennzeichen: string;
  geschaedigtes_kfz_typ: string;
  geschaedigtes_kfz_klasse: string;
  geschaedigtes_kfz_fin: string;
  geschaedigtes_kfz_hsn_tsn: string;
  miet_kfz_kennzeichen: string;
  miet_kfz_typ: string;
  miet_kfz_klasse: string;
  miet_kfz_fin: string;
  miet_kfz_hsn_tsn: string;
  // Step 4: Konditionen
  mietbeginn: string;
  mietbeginn_uhrzeit: string;
  mietende: string;
  tagessatz: number;
  mietdauer_tage: number;
  km_pauschale: number | null;
  mehr_km: number | null;
  kaution: number | null;
  sb_stufe: string;
  versicherungs_paket: string;
  selbstbeteiligung: number;
  // Step 5: Übergabe-Protokoll (alle optional)
  uebergabe_ort: string;
  uebergabe_datum: string;
  uebergabe_uhrzeit: string;
  uebergabe_km: string;
  uebergabe_tank: string;
  uebergabe_vorschaeden: string;
  uebergabe_fotos_urls: string[];
  rueckgabe_ort: string;
  rueckgabe_datum: string;
  rueckgabe_km: string;
  rueckgabe_tank: string;
  // Step 6: Unterschrift & Erzeugung
  checkbox_mietbedingungen: boolean;
  checkbox_abtretung: boolean;
  checkbox_datenschutz: boolean;
}

export const EMPTY_WIZARD_DATEN: WizardDaten = {
  mieter_vorname: '',
  mieter_nachname: '',
  mieter_strasse: '',
  mieter_plz: '',
  mieter_ort: '',
  mieter_telefon: '',
  mieter_email: '',
  mieter_geburtsdatum: '',
  mieter_fuehrerschein_nr: '',
  fahrer_abweichend: false,
  fahrer_vorname: '',
  fahrer_nachname: '',
  fahrer_fuehrerschein_nr: '',
  fahrer_geburtsdatum: '',
  versicherung_name: '',
  versicherung_adresse: '',
  versicherung_plz_ort: '',
  schadennummer: '',
  unfalldatum: '',
  unfallort: '',
  haftungsquote: 100,
  geschaedigtes_kfz_kennzeichen: '',
  geschaedigtes_kfz_typ: '',
  geschaedigtes_kfz_klasse: '',
  geschaedigtes_kfz_fin: '',
  geschaedigtes_kfz_hsn_tsn: '',
  miet_kfz_kennzeichen: '',
  miet_kfz_typ: '',
  miet_kfz_klasse: '',
  miet_kfz_fin: '',
  miet_kfz_hsn_tsn: '',
  mietbeginn: '',
  mietbeginn_uhrzeit: '',
  mietende: '',
  tagessatz: 65,
  mietdauer_tage: 0,
  km_pauschale: null,
  mehr_km: null,
  kaution: null,
  sb_stufe: '',
  versicherungs_paket: '',
  selbstbeteiligung: 0,
  uebergabe_ort: '',
  uebergabe_datum: '',
  uebergabe_uhrzeit: '',
  uebergabe_km: '',
  uebergabe_tank: '',
  uebergabe_vorschaeden: '',
  uebergabe_fotos_urls: [],
  rueckgabe_ort: '',
  rueckgabe_datum: '',
  rueckgabe_km: '',
  rueckgabe_tank: '',
  checkbox_mietbedingungen: false,
  checkbox_abtretung: false,
  checkbox_datenschutz: false,
};

const nonEmpty = (v: string) => v.trim().length > 0;

export function getMissingFields(step: StepNumber, daten: WizardDaten): string[] {
  switch (step) {
    case 1: {
      const missing: string[] = [];
      if (!nonEmpty(daten.mieter_vorname)) missing.push('mieter_vorname');
      if (!nonEmpty(daten.mieter_nachname)) missing.push('mieter_nachname');
      if (!nonEmpty(daten.mieter_strasse)) missing.push('mieter_strasse');
      if (!nonEmpty(daten.mieter_plz)) missing.push('mieter_plz');
      if (!nonEmpty(daten.mieter_ort)) missing.push('mieter_ort');
      if (!nonEmpty(daten.mieter_geburtsdatum)) missing.push('mieter_geburtsdatum');
      if (!nonEmpty(daten.mieter_fuehrerschein_nr)) missing.push('mieter_fuehrerschein_nr');
      if (daten.fahrer_abweichend) {
        if (!nonEmpty(daten.fahrer_vorname)) missing.push('fahrer_vorname');
        if (!nonEmpty(daten.fahrer_nachname)) missing.push('fahrer_nachname');
        if (!nonEmpty(daten.fahrer_fuehrerschein_nr)) missing.push('fahrer_fuehrerschein_nr');
        if (!nonEmpty(daten.fahrer_geburtsdatum)) missing.push('fahrer_geburtsdatum');
      }
      return missing;
    }
    case 2: {
      const missing: string[] = [];
      if (!nonEmpty(daten.versicherung_name)) missing.push('versicherung_name');
      if (!nonEmpty(daten.schadennummer)) missing.push('schadennummer');
      if (!nonEmpty(daten.unfalldatum)) missing.push('unfalldatum');
      return missing;
    }
    case 3: {
      const missing: string[] = [];
      if (!nonEmpty(daten.geschaedigtes_kfz_kennzeichen)) missing.push('geschaedigtes_kfz_kennzeichen');
      if (!nonEmpty(daten.geschaedigtes_kfz_klasse)) missing.push('geschaedigtes_kfz_klasse');
      if (!nonEmpty(daten.miet_kfz_kennzeichen)) missing.push('miet_kfz_kennzeichen');
      if (!nonEmpty(daten.miet_kfz_klasse)) missing.push('miet_kfz_klasse');
      return missing;
    }
    case 4: {
      const missing: string[] = [];
      if (!nonEmpty(daten.mietbeginn)) missing.push('mietbeginn');
      if (!nonEmpty(daten.mietbeginn_uhrzeit)) missing.push('mietbeginn_uhrzeit');
      if (!daten.tagessatz || daten.tagessatz <= 0) missing.push('tagessatz');
      return missing;
    }
    case 5:
      return [];
    case 6: {
      const missing: string[] = [];
      if (!daten.checkbox_mietbedingungen) missing.push('checkbox_mietbedingungen');
      if (!daten.checkbox_abtretung) missing.push('checkbox_abtretung');
      if (!daten.checkbox_datenschutz) missing.push('checkbox_datenschutz');
      return missing;
    }
  }
}

export function isStepValid(step: StepNumber, daten: WizardDaten): boolean {
  return getMissingFields(step, daten).length === 0;
}

export function clearFahrerFields(daten: WizardDaten): WizardDaten {
  return {
    ...daten,
    fahrer_vorname: '',
    fahrer_nachname: '',
    fahrer_fuehrerschein_nr: '',
    fahrer_geburtsdatum: '',
  };
}

export function buildWizardDatenFromAkte(akte: Record<string, unknown>): WizardDaten {
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const num = (v: unknown, fallback: number) => (typeof v === 'number' ? v : fallback);

  return {
    ...EMPTY_WIZARD_DATEN,
    mieter_vorname: str(akte.anspruchsteller_vorname) || str(akte.kunde_name)?.split(' ').slice(0, -1).join(' ') || '',
    mieter_nachname: str(akte.anspruchsteller_nachname) || str(akte.kunde_name)?.split(' ').pop() || '',
    mieter_strasse: str(akte.anspruchsteller_strasse),
    mieter_plz: str(akte.anspruchsteller_plz),
    mieter_ort: str(akte.anspruchsteller_ort),
    mieter_telefon: str(akte.anspruchsteller_telefon),
    mieter_email: str(akte.anspruchsteller_email),
    mieter_geburtsdatum: str(akte.anspruchsteller_geburtsdatum),
    mieter_fuehrerschein_nr: str(akte.fahrer_fuehrerschein_nr),
    fahrer_vorname: str(akte.fahrer_vorname),
    fahrer_nachname: str(akte.fahrer_nachname),
    fahrer_fuehrerschein_nr: str(akte.fahrer_fuehrerschein_nr),
    fahrer_geburtsdatum: str(akte.fahrer_geburtsdatum),
    versicherung_name: str(akte.versicherung),
    versicherung_adresse: str(akte.versicherung_adresse),
    versicherung_plz_ort: [akte.versicherung_plz, akte.versicherung_ort].filter(Boolean).join(' '),
    schadennummer: str(akte.schadennummer),
    unfalldatum: str(akte.unfalldatum),
    unfallort: str(akte.unfallort),
    haftungsquote: num(akte.haftungsquote, 100),
    geschaedigtes_kfz_kennzeichen: str(akte.kennzeichen),
    geschaedigtes_kfz_typ: str(akte.fahrzeug_typ),
    geschaedigtes_kfz_klasse: str(akte.fahrzeug_klasse),
    miet_kfz_kennzeichen: str(akte.miet_kfz_kennzeichen),
    miet_kfz_typ: str(akte.miet_kfz_typ),
    miet_kfz_klasse: str(akte.miet_kfz_klasse),
    mietbeginn: str(akte.mietbeginn),
    mietende: str(akte.mietende),
    tagessatz: num(akte.tagessatz, 65),
    mietdauer_tage: num(akte.mietdauer_tage, 0),
    selbstbeteiligung: num(akte.selbstbeteiligung, 0),
    uebergabe_km: str(akte.uebergabe_km_stand),
    uebergabe_tank: str(akte.uebergabe_tankstand),
    uebergabe_datum: str(akte.uebergabe_datum),
    uebergabe_ort: str(akte.uebergabe_ort),
    uebergabe_fotos_urls: Array.isArray(akte.uebergabe_fotos_urls) ? akte.uebergabe_fotos_urls : [],
    rueckgabe_km: str(akte.rueckgabe_km_stand),
    rueckgabe_tank: str(akte.rueckgabe_tankstand),
    rueckgabe_datum: str(akte.rueckgabe_datum),
    rueckgabe_ort: str(akte.rueckgabe_ort),
  };
}

export function buildWizardDatenFromGutachtenAkte(gutachtenAkte: Record<string, unknown>): WizardDaten {
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const num = (v: unknown, fallback: number) => (typeof v === 'number' && v > 0 ? v : fallback);

  const fullName = str(gutachtenAkte.anspruchsteller_name).trim();
  const nameParts = fullName ? fullName.split(' ') : [];
  const vorname = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : fullName;
  const nachname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

  const fahrzeugTyp = [str(gutachtenAkte.fahrzeug_marke), str(gutachtenAkte.fahrzeug_modell)]
    .filter(Boolean)
    .join(' ');

  const mietdauer = num(gutachtenAkte.reparaturdauer_tage, 0) ||
    num(gutachtenAkte.voraussichtliche_reparaturdauer, 0);

  return {
    ...EMPTY_WIZARD_DATEN,
    mieter_vorname: vorname,
    mieter_nachname: nachname,
    mieter_strasse: str(gutachtenAkte.anspruchsteller_strasse) || str(gutachtenAkte.anspruchsteller_adresse),
    mieter_plz: str(gutachtenAkte.anspruchsteller_plz),
    mieter_ort: str(gutachtenAkte.anspruchsteller_ort),
    mieter_telefon: str(gutachtenAkte.anspruchsteller_telefon),
    mieter_email: str(gutachtenAkte.anspruchsteller_email),
    mieter_geburtsdatum: str(gutachtenAkte.anspruchsteller_geburtsdatum),
    versicherung_name: str(gutachtenAkte.versicherung),
    versicherung_adresse: str(gutachtenAkte.versicherung_adresse),
    versicherung_plz_ort: [gutachtenAkte.versicherung_plz, gutachtenAkte.versicherung_ort]
      .filter(Boolean)
      .join(' '),
    schadennummer: str(gutachtenAkte.schadennummer),
    unfalldatum: str(gutachtenAkte.unfalldatum) || str(gutachtenAkte.schadentag),
    unfallort: str(gutachtenAkte.unfallort) || str(gutachtenAkte.schadenort),
    haftungsquote: 100,
    geschaedigtes_kfz_kennzeichen: str(gutachtenAkte.fahrzeug_kennzeichen) || str(gutachtenAkte.kennzeichen),
    geschaedigtes_kfz_typ: fahrzeugTyp,
    geschaedigtes_kfz_klasse: str(gutachtenAkte.fahrzeug_klasse),
    tagessatz: 65,
    mietdauer_tage: mietdauer,
  };
}

export function isGutachtenTotalschaden(gutachtenAkte: Record<string, unknown>): boolean {
  return gutachtenAkte.fahrzeug_totalschaden === true;
}

const DRAFT_KEY = (akteId: string) => `mietwagen-wizard-draft-${akteId}`;

export interface WizardDraft {
  daten: Partial<WizardDaten>;
  step: StepNumber;
}

export function saveDraft(akteId: string, daten: WizardDaten, step: StepNumber): void {
  try {
    localStorage.setItem(DRAFT_KEY(akteId), JSON.stringify({ daten, step }));
  } catch {
    // localStorage may be unavailable in some environments
  }
}

export function loadDraft(akteId: string): WizardDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(akteId));
    if (!raw) return null;
    return JSON.parse(raw) as WizardDraft;
  } catch {
    return null;
  }
}

export function clearDraft(akteId: string): void {
  try {
    localStorage.removeItem(DRAFT_KEY(akteId));
  } catch {
    // ignore
  }
}
